import React, { FC, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import { Typography } from "@mui/material";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";

import { AddressAutocompleteParameters, FormFieldTextAddressInput } from "stentor-models";

import { FormTextInputProps } from "../FormFieldTextInput/FormFieldTextInput";
import { AddressAutocompleteService, PlaceType } from "../../utils/AddressAutocompleteService";

// import Popper, { PopperProps } from '@mui/material/Popper';

import styles from "./FormFieldTextInputAddress.module.scss";

// const CustomPopper = (props: JSX.IntrinsicAttributes & PopperProps & React.RefAttributes<HTMLDivElement>) => {
//     return <Popper {...props} className={styles.xapp_text_input_address_autocomplete_popper} />;
// };

const POWERED_BY_GOOGLE_URL =
    "https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png";

// Stable component reference to avoid dropdown remounting on every render
const GoogleAttributedPaper: FC<React.HTMLAttributes<HTMLElement>> = ({
    children,
    ...paperProps
}) => (
    <Paper {...paperProps}>
        {children}
        <Box sx={{ textAlign: "right", padding: "4px 12px 8px" }}>
            <img src={POWERED_BY_GOOGLE_URL} alt="powered by Google" style={{ height: 14 }} />
        </Box>
    </Paper>
);

export interface FormFieldTextInputAddressProps
    extends FormTextInputProps,
        Pick<FormFieldTextAddressInput, "googleMapsApiKey" | "mapsBaseUrl"> {
    readonly field: FormFieldTextAddressInput;
    /**
     * When true, the user must select an address from the autocomplete suggestions.
     * Free-text entry is not allowed.
     *
     * Can also be set via `field.requireSelection` (field-level takes precedence).
     * @see https://github.com/stentorium/stentor/issues/3040
     */
    readonly requireSelection?: boolean;
}

export const FormFieldTextInputAddress: FC<FormFieldTextInputAddressProps> = (props) => {
    const {
        field,
        onFieldChange,
        errorMessage,
        required,
        googleMapsApiKey,
        mapsBaseUrl,
        requireSelection,
    } = props;

    // Field-level takes precedence, then component prop, default false
    const isFreeSolo = !(field.requireSelection ?? requireSelection);

    const [address, setAddress] = useState<PlaceType | string | null>(null);
    const [text, setText] = useState("");
    const [options, setOptions] = useState<readonly PlaceType[]>([]);

    // Mirrors the current input text so async place details callbacks can tell
    // whether the customer has typed or reselected since the request was made.
    const textRef = useRef("");

    const onAddressChange = (a: PlaceType | string | null) => {
        setAddress(a);
    };

    const onTextChange = (t: string) => {
        textRef.current = t;
        setText(t);
    };

    const onOptionsChange = (o: readonly PlaceType[]) => {
        setOptions(o);
    };

    useEffect(() => {
        onFieldChange(field.name, text);
    }, [field.name, onFieldChange, text]);

    const apiKey = field.googleMapsApiKey || googleMapsApiKey;
    const baseUrl = field.mapsBaseUrl || mapsBaseUrl;

    const autocompleteService = useMemo(
        () => new AddressAutocompleteService({ apiKey, baseUrl }),
        [apiKey, baseUrl]
    );

    // Show Google attribution when using the xapp.ai proxy (which forwards to Google Places API).
    // Custom non-xapp.ai base URLs are assumed to be non-Google backends that handle their own attribution.
    const showGoogleAttribution = (() => {
        if (!baseUrl) return true;
        try {
            const hostname = new URL(baseUrl).hostname;
            return hostname === "xapp.ai" || hostname.endsWith(".xapp.ai");
        } catch {
            return false;
        }
    })();

    const fetch = useMemo(
        () =>
            debounce(
                (
                    request: { input: string; params?: AddressAutocompleteParameters },
                    callback: (results?: readonly PlaceType[]) => void
                ) => {
                    autocompleteService.getPlacePredictions(request, callback);
                },
                400
            ),
        [autocompleteService]
    );

    useEffect(() => {
        let active = true;

        if (text === "") {
            onOptionsChange(address && typeof address !== "string" ? [address] : []);
            return undefined;
        }

        const queryParams = field.mapsUrlQueryParams || undefined;

        fetch({ input: text, params: queryParams }, (results?: readonly PlaceType[]) => {
            if (active) {
                let newOptions: readonly PlaceType[] = [];

                if (address && typeof address !== "string") {
                    newOptions = [address];
                }

                if (results) {
                    newOptions = [...newOptions, ...results];
                }

                onOptionsChange(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }, [address, text, fetch, field.mapsUrlQueryParams]);

    return (
        <Autocomplete
            id="google-map-input"
            // PopperComponent={CustomPopper}
            // sx={{ width: 300 }}
            {...(showGoogleAttribution && { PaperComponent: GoogleAttributedPaper })}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.description)}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            freeSolo={isFreeSolo}
            includeInputInList
            filterSelectedOptions
            value={address}
            inputValue={text}
            noOptionsText="No locations"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={(_event: any, newValue: PlaceType | string | null) => {
                if (typeof newValue === "string") {
                    onOptionsChange([]);
                } else {
                    onOptionsChange(newValue ? [newValue, ...options] : options);
                }
                onAddressChange(newValue);
                // Resolve the selected prediction to full place details so the displayed
                // and stored address includes the postal code (predictions never carry one).
                if (newValue && typeof newValue !== "string" && newValue.placeId) {
                    const { placeId, description } = newValue;
                    onTextChange(description);
                    autocompleteService.getPlaceDetails(placeId, (details) => {
                        // Skip stale results: only apply if the field still shows this prediction
                        if (details?.formattedAddress && textRef.current === description) {
                            onAddressChange({ ...newValue, description: details.formattedAddress });
                            onTextChange(details.formattedAddress);
                        }
                    });
                }
            }}
            onInputChange={(_event, newInputValue) => {
                onTextChange(newInputValue);
            }}
            renderInput={(params) => (
                <TextField
                    className={styles.xapp_text_input_address}
                    {...params}
                    variant="filled"
                    size="small"
                    margin="normal"
                    label={field.label}
                    error={!!errorMessage}
                    helperText={errorMessage}
                    name={field.name}
                    placeholder={field.placeholder}
                    required={required}
                    fullWidth
                    style={field.style}
                    slotProps={
                        field.maxLength
                            ? { htmlInput: { ...params.inputProps, maxLength: field.maxLength } }
                            : undefined
                    }
                />
            )}
            renderOption={(props, option) => {
                if (typeof option === "string") {
                    return <li {...props}>{option}</li>;
                }
                const matches = option.structured_formatting.main_text_matched_substrings || [];

                const parts = parse(
                    option.structured_formatting.main_text,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    matches.map((match: any) => [match.offset, match.offset + match.length])
                );

                return (
                    <li {...props}>
                        <Grid container alignItems="center">
                            <Grid sx={{ display: "flex", width: 44 }}>
                                <LocationOnIcon sx={{ color: "text.secondary" }} />
                            </Grid>
                            <Grid sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}>
                                {parts.map((part, index) => (
                                    <Box
                                        key={index}
                                        component="span"
                                        sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                                    >
                                        {part.text}
                                    </Box>
                                ))}
                                <Typography variant="body2" color="text.secondary">
                                    {option.structured_formatting.secondary_text}
                                </Typography>
                            </Grid>
                        </Grid>
                    </li>
                );
            }}
        />
    );
};
